import React, { useState, useEffect, useCallback } from 'react';
import { TrendingUp, ChevronDown, ChevronUp, BarChart2 } from 'lucide-react';
import { marketApi } from '../../services/marketApi';

const DIMENSION_OPTIONS = [
  { value: 'model', label: 'Modele' },
  { value: 'make', label: 'Marque' },
  { value: 'body_type', label: 'Carrosserie' },
  { value: 'fuel_type', label: 'Carburant' },
  { value: 'price_bracket', label: 'Tranche de prix' },
];

const PERIOD_OPTIONS = [
  { value: 3, label: '3 mois' },
  { value: 6, label: '6 mois' },
  { value: 12, label: '12 mois' },
  { value: 24, label: '24 mois' },
];

const SORT_OPTIONS = [
  { value: 'profitability_score', label: 'Score rentabilite' },
  { value: 'margin_pct', label: 'Marge %' },
  { value: 'velocity_days', label: 'Rotation' },
  { value: 'count', label: 'Volume' },
];

const PAGE_LIMIT = 20;

const inputClass = 'w-full bg-dark-hover border border-dark-border rounded-lg px-3 py-2 text-primary text-sm focus:outline-none focus:border-carmin transition-colors';
const labelClass = 'block text-xs font-medium text-secondary mb-1';

const BRACKET_COLORS = {
  'negative': 'bg-red-500',
  '0-5%': 'bg-amber-500',
  '5-10%': 'bg-yellow-500',
  '10-15%': 'bg-lime-500',
  '15-20%': 'bg-emerald-500',
  '20%+': 'bg-green-500',
};

const BRACKET_LABELS = {
  'negative': 'Negative',
  '0-5%': '0-5%',
  '5-10%': '5-10%',
  '10-15%': '10-15%',
  '15-20%': '15-20%',
  '20%+': '20%+',
};

const scoreBg = (score) => {
  if (score > 70) return 'bg-green-500/20 text-green-400 border-green-500/30';
  if (score >= 50) return 'bg-amber-500/20 text-amber-400 border-amber-500/30';
  return 'bg-red-500/20 text-red-400 border-red-500/30';
};

const Spinner = () => (
  <div className="flex justify-center py-10">
    <span className="h-6 w-6 border-2 border-carmin/30 border-t-carmin rounded-full animate-spin" />
  </div>
);

const MarginDistributionBar = ({ distribution }) => {
  if (!distribution || distribution.length === 0) return null;

  return (
    <div className="space-y-2 px-4 pb-4">
      <div className="text-xs text-muted font-medium mb-1">Distribution des marges</div>
      <div className="flex h-4 rounded-full overflow-hidden">
        {distribution.map((segment, i) => {
          if (segment.pct <= 0) return null;
          const color = BRACKET_COLORS[segment.bracket] || 'bg-gray-500';
          return (
            <div
              key={segment.bracket}
              className={`${color} relative group`}
              style={{ width: `${segment.pct}%` }}
              title={`${BRACKET_LABELS[segment.bracket] || segment.bracket}: ${segment.pct.toFixed(1)}%`}
            />
          );
        })}
      </div>
      <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1">
        {distribution.map((segment) => {
          if (segment.pct <= 0) return null;
          const color = BRACKET_COLORS[segment.bracket] || 'bg-gray-500';
          return (
            <div key={segment.bracket} className="flex items-center gap-1.5 text-[10px] text-secondary">
              <span className={`inline-block w-2.5 h-2.5 rounded-sm ${color}`} />
              <span>{BRACKET_LABELS[segment.bracket] || segment.bracket}</span>
              <span className="text-muted">{segment.pct.toFixed(1)}%</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const ProfitabilityTab = () => {
  const [dimension, setDimension] = useState('model');
  const [makeFilter, setMakeFilter] = useState('');
  const [bodyTypeFilter, setBodyTypeFilter] = useState('');
  const [periodMonths, setPeriodMonths] = useState(6);
  const [sortBy, setSortBy] = useState('profitability_score');
  const [offset, setOffset] = useState(0);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [expandedRows, setExpandedRows] = useState({});

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = {
        dimension,
        period_months: periodMonths,
        sort_by: sortBy,
        limit: PAGE_LIMIT,
        offset,
      };
      if (makeFilter.trim()) params.make = makeFilter.trim();
      if (bodyTypeFilter.trim()) params.body_type = bodyTypeFilter.trim();
      const result = await marketApi.getProfitability(params);
      setData(result);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur lors du chargement de la rentabilite');
    } finally {
      setLoading(false);
    }
  }, [dimension, periodMonths, sortBy, offset, makeFilter, bodyTypeFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleFilterChange = (setter) => (val) => {
    setter(val);
    setOffset(0);
    setExpandedRows({});
  };

  const toggleRow = (index) => {
    setExpandedRows((prev) => ({ ...prev, [index]: !prev[index] }));
  };

  const total = data?.total ?? 0;
  const hasNext = offset + PAGE_LIMIT < total;
  const hasPrev = offset > 0;

  return (
    <div className="space-y-6">
      <div className="bg-dark-card border border-dark-border rounded-xl p-5 space-y-4">
        <div className="flex items-center gap-2 text-primary font-semibold text-sm">
          <BarChart2 className="h-4 w-4 text-carmin" />
          Analyse de rentabilite
        </div>

        {/* Filters */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          <div>
            <label className={labelClass}>Dimension</label>
            <select
              value={dimension}
              onChange={(e) => handleFilterChange(setDimension)(e.target.value)}
              className={inputClass}
            >
              {DIMENSION_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className={labelClass}>Marque</label>
            <input
              type="text"
              value={makeFilter}
              onChange={(e) => handleFilterChange(setMakeFilter)(e.target.value)}
              placeholder="ex: BMW"
              className={inputClass}
            />
          </div>
          <div>
            <label className={labelClass}>Carrosserie</label>
            <input
              type="text"
              value={bodyTypeFilter}
              onChange={(e) => handleFilterChange(setBodyTypeFilter)(e.target.value)}
              placeholder="ex: SUV"
              className={inputClass}
            />
          </div>
          <div>
            <label className={labelClass}>Periode</label>
            <select
              value={periodMonths}
              onChange={(e) => handleFilterChange(setPeriodMonths)(Number(e.target.value))}
              className={inputClass}
            >
              {PERIOD_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className={labelClass}>Trier par</label>
            <select
              value={sortBy}
              onChange={(e) => handleFilterChange(setSortBy)(e.target.value)}
              className={inputClass}
            >
              {SORT_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
          <div className="flex items-end">
            <span className="text-xs text-muted pb-2">
              {total} resultat{total !== 1 ? 's' : ''}
            </span>
          </div>
        </div>

        {/* Table */}
        {loading ? (
          <Spinner />
        ) : error ? (
          <div className="bg-red-500/10 border border-red-500/30 rounded-lg px-4 py-3 text-red-400 text-sm">
            {error}
          </div>
        ) : data?.results?.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-dark-border text-left">
                  <th className="pb-2 text-xs font-medium text-muted">Segment</th>
                  <th className="pb-2 text-xs font-medium text-muted text-right">
                    <span className="inline-flex items-center gap-1"><TrendingUp className="h-3 w-3" /> Score</span>
                  </th>
                  <th className="pb-2 text-xs font-medium text-muted text-right">Marge med. %</th>
                  <th className="pb-2 text-xs font-medium text-muted text-right">Rotation med.</th>
                  <th className="pb-2 text-xs font-medium text-muted text-right">Volume</th>
                  <th className="pb-2 text-xs font-medium text-muted w-10" />
                </tr>
              </thead>
              <tbody className="divide-y divide-dark-border">
                {data.results.map((row, i) => (
                  <React.Fragment key={i}>
                    <tr
                      className="hover:bg-dark-hover/50 transition-colors cursor-pointer"
                      onClick={() => toggleRow(i)}
                    >
                      <td className="py-2.5 text-primary font-medium">{row.group}</td>
                      <td className="py-2.5 text-right">
                        <span className={`inline-block text-xs font-semibold px-2 py-0.5 rounded-full border ${scoreBg(row.profitability_score)}`}>
                          {row.profitability_score ?? '-'}
                        </span>
                      </td>
                      <td className="py-2.5 text-right text-secondary">
                        {row.margin_pct?.median != null ? `${row.margin_pct.median.toFixed(1)}%` : '-'}
                      </td>
                      <td className="py-2.5 text-right text-secondary">
                        {row.velocity_days?.median != null ? `${row.velocity_days.median}j` : '-'}
                      </td>
                      <td className="py-2.5 text-right text-secondary">
                        {row.count ?? '-'}
                      </td>
                      <td className="py-2.5 text-center">
                        {expandedRows[i]
                          ? <ChevronUp className="h-4 w-4 text-muted inline-block" />
                          : <ChevronDown className="h-4 w-4 text-muted inline-block" />
                        }
                      </td>
                    </tr>
                    {expandedRows[i] && (
                      <tr>
                        <td colSpan={6} className="bg-dark-hover/30">
                          <MarginDistributionBar distribution={row.margin_distribution} />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-center text-muted text-sm py-6">Aucune donnee disponible</p>
        )}

        {/* Pagination */}
        {total > PAGE_LIMIT && (
          <div className="flex items-center justify-between pt-2">
            <button
              onClick={() => setOffset(Math.max(0, offset - PAGE_LIMIT))}
              disabled={!hasPrev}
              className="text-xs text-secondary hover:text-primary disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              Precedent
            </button>
            <span className="text-xs text-muted">
              {offset + 1} - {Math.min(offset + PAGE_LIMIT, total)} sur {total}
            </span>
            <button
              onClick={() => setOffset(offset + PAGE_LIMIT)}
              disabled={!hasNext}
              className="text-xs text-secondary hover:text-primary disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              Suivant
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProfitabilityTab;
